// @flow

import React, { type Node } from 'react';
import { useUniqueId } from '@tectonics/hooks';
import { InputMask, formatValueWithMask } from '@tectonics/components/input-mask';
import { useUserInputAsTextFieldWithFormatting } from '@tectonics/prs/user-input-values';
import { Label, TextField } from '../../../../../../commons/atoms';
import { InputGroup } from '../../../../../../commons/molecules';
import { useUserInputValidationError, type UserInputValidationError, serverErrorMessage } from '@tectonics/prs/validation';
import { normalizeCustomerDocument } from '@tectonics/prs/customer/chile/normalize';
import { useFormHasBeenSent } from '@tectonics/prs/form-status';
import { useSubmitting } from '@tectonics/prs/submitting';
import { ErrorLabel, InputHolder } from '../../commons';

export function InputChileanDocument(): Node {
  const inputId = useUniqueId();
  const submitting = useSubmitting();

  const [document, handleDocumentChange] = useUserInputAsTextFieldWithFormatting(
    'chileanDocument',
    formatRaw,
    normalizeCustomerDocument,
  );

  const formHasBeenSent = useFormHasBeenSent();
  const validationError = useUserInputValidationError('chileanDocument');

  return (
    <InputHolder data-test-name="customer-document">
      <InputGroup
        label={<Label htmlFor={inputId}>RUT</Label>}
        input={
          <TextField
            as={InputMask}
            id={inputId}
            value={document}
            mask={RUT_MASK}
            onChange={handleDocumentChange}
            fashion={formHasBeenSent && validationError ? 'error' : null}
            readOnly={submitting}
            data-error={formHasBeenSent && validationError ? 'error' : null}
            name="customer-document"
          />
        }
      />
      {formHasBeenSent && validationError && (
        <ValidationErrorMessage validationError={validationError} />
      )}
    </InputHolder>
  );
}

function formatRaw(rawValue: string): string {
  return formatValueWithMask(rawValue, RUT_MASK);
}

const RUT_MASK = [
  /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/, /[0-9K]/i, /[0-9K]/i,
];

type ValidationErrorMessageProps = {|
  validationError: UserInputValidationError,
|};

function ValidationErrorMessage({ validationError }: ValidationErrorMessageProps): Node {
  switch (validationError.id) {
    case 'empty':
      return <ErrorLabel>El documento es requerido</ErrorLabel>;
    case 'server-error':
      return <ErrorLabel>{serverErrorMessage(validationError.payload)}</ErrorLabel>;
    default:
      return <ErrorLabel>El documento informado es inválido</ErrorLabel>;
  }
}
